import {
  CopyableCodeBlock,
  CopyableValue,
  DescriptionList,
  EmptyState,
  Stack,
  Tabs,
  Tag,
  Title,
  type TabItem,
} from "./controls";
import { useFeedback } from "./feedback";
import "./FaaSFunctionDetail.css";

export interface FaaSFunctionDetailData {
  allowedSubjectTypes: string[];
  entrypoint?: string;
  functionDescription: string;
  functionDisplayName: string;
  functionSlug: string;
  functionVersion: string;
  manifest: unknown;
  manifestDigest: string;
  maxAttempts: number;
  maxInputBytes: number;
  maxOutputBytes: number;
  requestedCapabilities: string[];
  runtimeKind: string;
  source?: string;
  timeoutMs: number;
  tool: {
    annotations?: Record<string, boolean>;
    description: string;
    executionPolicy?: {
      agent?: string;
    };
    inputSchema: Record<string, unknown>;
    name: string;
    outputSchema?: Record<string, unknown>;
  };
}

export function FaaSFunctionDetail({
  detail,
}: {
  detail: FaaSFunctionDetailData;
}) {
  const { message } = useFeedback();
  const copyFeedback = {
    onCopied: () => void message.success("已复制"),
    onCopyError: () => void message.error("复制失败"),
  };
  const allowedSubjectTypes = detail.allowedSubjectTypes ?? [];
  const requestedCapabilities = detail.requestedCapabilities ?? [];
  const overview = (
    <>
      <p className="faas-detail-description">
        {detail.functionDescription || detail.tool.description}
      </p>
      <DescriptionList
        className="faas-detail-metadata"
        grouped
        items={[
          {
            key: "slug",
            term: "函数标识",
            detail: <code>{detail.functionSlug}</code>,
          },
          {
            key: "version",
            term: "固定版本",
            detail: <code>{detail.functionVersion}</code>,
          },
          {
            key: "tool",
            term: "Tool Name",
            detail: <code>{detail.tool.name}</code>,
          },
          {
            key: "runtime",
            term: "Runtime",
            detail: <Tag>{detail.runtimeKind}</Tag>,
          },
          {
            key: "agent",
            term: "Agent 策略",
            detail: (
              <Tag
                color={
                  detail.tool.executionPolicy?.agent === "confirm"
                    ? "orange"
                    : "green"
                }
              >
                {detail.tool.executionPolicy?.agent ?? "auto"}
              </Tag>
            ),
          },
          { key: "attempts", term: "最大重试", detail: detail.maxAttempts },
          {
            key: "timeout",
            term: "超时",
            detail: formatDuration(detail.timeoutMs),
          },
          {
            key: "limits",
            term: "输入 / 输出上限",
            detail: (
              <>
                {formatBytes(detail.maxInputBytes)} /{" "}
                {formatBytes(detail.maxOutputBytes)}
              </>
            ),
          },
          ...(detail.entrypoint
            ? [
                {
                  key: "entrypoint",
                  term: "入口",
                  detail: <code>{detail.entrypoint}</code>,
                },
              ]
            : []),
          {
            key: "manifest",
            term: "Manifest 摘要",
            detail: (
              <CopyableValue
                {...copyFeedback}
                label="Manifest 摘要"
                value={detail.manifestDigest}
              />
            ),
            itemProps: { className: "faas-detail-wide" },
          },
          {
            key: "subjects",
            term: "允许主体",
            detail: (
              <Stack gap={4} wrap>
                {allowedSubjectTypes.length > 0
                  ? allowedSubjectTypes.map((item) => (
                      <Tag key={item}>{item}</Tag>
                    ))
                  : "-"}
              </Stack>
            ),
            itemProps: { className: "faas-detail-wide" },
          },
          {
            key: "capabilities",
            term: "业务权限",
            detail: (
              <Stack gap={4} wrap>
                {requestedCapabilities.length > 0
                  ? requestedCapabilities.map((item) => (
                      <Tag key={item}>{item}</Tag>
                    ))
                  : "-"}
              </Stack>
            ),
            itemProps: { className: "faas-detail-wide" },
          },
          {
            key: "annotations",
            term: "行为标记",
            detail: (
              <Stack gap={4} wrap>
                {annotationLabels(detail.tool.annotations).map((item) => (
                  <Tag key={item}>{item}</Tag>
                ))}
              </Stack>
            ),
            itemProps: { className: "faas-detail-wide" },
          },
        ]}
      />
    </>
  );
  const items: TabItem[] = [
    { children: overview, key: "overview", label: "概览" },
    {
      children: (
        <CopyableCodeBlock
          {...copyFeedback}
          content={JSON.stringify(detail.manifest ?? {}, null, 2)}
          label="function.manifest.json"
        />
      ),
      key: "manifest",
      label: "Manifest",
    },
    {
      children: (
        <CopyableCodeBlock
          {...copyFeedback}
          content={JSON.stringify(detail.tool.inputSchema, null, 2)}
          label="input.schema.json"
        />
      ),
      key: "input",
      label: "输入 Schema",
    },
    {
      children: detail.tool.outputSchema ? (
        <CopyableCodeBlock
          {...copyFeedback}
          content={JSON.stringify(detail.tool.outputSchema, null, 2)}
          label="output.schema.json"
        />
      ) : (
        <EmptyState description="未声明输出 Schema" />
      ),
      key: "output",
      label: "输出 Schema",
    },
  ];
  items.push({
    children:
      detail.runtimeKind === "deno" ? (
        detail.source !== undefined ? (
          <CopyableCodeBlock
            {...copyFeedback}
            content={detail.source}
            label={detail.entrypoint ?? "mod.ts"}
          />
        ) : (
          <EmptyState description="源码不可用" />
        )
      ) : (
        <CopyableCodeBlock
          {...copyFeedback}
          content={
            JSON.stringify(runtimeDefinition(detail.manifest), null, 2) ?? "{}"
          }
          label={`${detail.runtimeKind}.runtime.json`}
        />
      ),
    key: "implementation",
    label: "实现",
  });

  return (
    <section className="faas-detail" aria-label="函数详情">
      <header className="faas-detail-header">
        <Title level={4}>
          {detail.functionDisplayName || detail.functionSlug}
        </Title>
        <Tag>{detail.functionVersion}</Tag>
      </header>
      <Tabs aria-label="函数详情选项卡" items={items} />
    </section>
  );
}

function annotationLabels(annotations?: Record<string, boolean>) {
  const labels: Record<string, string> = {
    destructiveHint: "破坏性",
    idempotentHint: "幂等",
    openWorldHint: "访问外部资源",
    readOnlyHint: "只读",
  };
  const items = Object.entries(annotations ?? {})
    .filter(([, enabled]) => enabled)
    .map(([key]) => labels[key] ?? key);
  return items.length > 0 ? items : ["无"];
}

function runtimeDefinition(manifest: unknown): unknown {
  if (!manifest || typeof manifest !== "object" || !("spec" in manifest)) {
    return {};
  }
  const spec = manifest.spec;
  if (!spec || typeof spec !== "object" || !("runtime" in spec)) return {};
  return spec.runtime;
}

function formatBytes(value: number) {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KiB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MiB`;
}

function formatDuration(value: number) {
  return value >= 1000 && value % 1000 === 0
    ? `${value / 1000} s`
    : `${value} ms`;
}
